import { z } from 'zod'

import { dateUtil, isNull, r } from '../../util'

export const millsheetCsvColumnMapper = {
  constructionName: '工事名称',
  constructionArea: '工区',
  member: '節',
  steelType: '規格',
  sectionType: '形状',
  manufacturerName: 'メーカー',
  tradingCompany: '商社',
  issuedDate: '発行日',
  no: '証明書番号',
  orderNumber: '注文番号',
  innerNo: '内部番号',
  customer: '需要家',
  size: '寸法',
  length: '長さ',
  quantity: '数量',
  weightBeforeProcessing: '重量',
  // weightAfterProcessing: '加工後重量',
  heatNumber: '製鋼番号',
  individualNumber: '個体番号',
}

export function validateAndNormalizeMillsheetCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // すべて空行だったらスキップ
  if (
    r(record)
      .values()
      .every((x) => x === '')
  ) {
    return {
      type: 'rebar' as const,
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // TODO: keysのvalidation

  // 必要なデータのみを一旦抽出する
  const data = {
    manufacturerName: record[millsheetCsvColumnMapper.manufacturerName],
    tradingCompany: record[millsheetCsvColumnMapper.tradingCompany],
    customer: record[millsheetCsvColumnMapper.customer],
    constructionArea: record[millsheetCsvColumnMapper.constructionArea],
    member: record[millsheetCsvColumnMapper.member],
    constructionName: record[millsheetCsvColumnMapper.constructionName],
    issuedDate: record[millsheetCsvColumnMapper.issuedDate],
    no: record[millsheetCsvColumnMapper.no],
    orderNumber: record[millsheetCsvColumnMapper.orderNumber],
    innerNo: record[millsheetCsvColumnMapper.innerNo],
    steelType: record[millsheetCsvColumnMapper.steelType],
    sectionType: record[millsheetCsvColumnMapper.sectionType],
    size: record[millsheetCsvColumnMapper.size],
    length: record[millsheetCsvColumnMapper.length],
    quantity: record[millsheetCsvColumnMapper.quantity],
    weightBeforeProcessing: record[millsheetCsvColumnMapper.weightBeforeProcessing],
    // weightAfterProcessing: record[millsheetCsvColumnMapper.weightAfterProcessing],
    heatNumber: record[millsheetCsvColumnMapper.heatNumber],
    individualNumber: record[millsheetCsvColumnMapper.individualNumber],
  }
  // schemaもハードコーディング
  const schema = z.object({
    manufacturerName: z.string(),
    tradingCompany: z.string().optional(),
    customer: z.string().optional(),
    constructionArea: z.string().optional(),
    member: z.string().optional(),
    constructionName: z.string().optional(),
    issuedDate: z.string(),
    no: z.string().optional(),
    orderNumber: z.string().optional(),
    innerNo: z.string().optional(),
    steelType: z.string(),
    sectionType: z.string(),
    size: z.string(),
    length: z.string().optional(),
    // quantityは数値の文字列（カンマ、小数点を許す）
    quantity: z
      .string()
      .regex(/^[0-9,.]+$/, { message: '数値で入力してください。' })
      .transform((x) => {
        return Number(x.replace(/,/g, ''))
      }),
    weightBeforeProcessing: z
      .string()
      .transform((x, ctx) => {
        const trimmed = x.trim()
        if (trimmed === '') {
          return null
        }
        if (!/^[0-9,]+$/.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        const parsed = Number(trimmed.replace(/,/g, ''))
        if (!Number.isInteger(parsed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        return parsed
      })
      .nullable(),
    // weightAfterProcessing: z
    //   .string()
    //   .regex(/^[0-9,.]*$/, { message: '数値で入力してください。' })
    //   .optional()
    //   .transform((x) => {
    //     if (isNull(x) || !isPresent(x)) {
    //       return undefined
    //     }
    //     return Number(x.replace(/,/g, ''))
    //   }),
    heatNumber: z.string(),
    individualNumber: z.string().optional(),
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = millsheetCsvColumnMapper[x.path.join('') as 'size'] ?? x.path.join('')
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: `日付形式がYYYY/MM/DDの形式になっていません。入力：${parsed.data.issuedDate}`,
      record,
      index,
    }
  }
  const result = {
    type: 'rebar' as const,
    ...parsed.data,
    rank: index + 1,
    issuedDate: dateParsed.date,
    raw: data,
  }

  return {
    type: 'rebar' as const,
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.heatNumber,
  }
}
